/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { Modal, Textarea, TextInput } from "@mantine/core";
import { useAuthInfo } from "@propelauth/react";
import { useMutation } from "@tanstack/react-query";
import React, {
  useMemo, useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { queryClient, useViews } from "../../../hooks/QueryHooks";
import { createView } from "../../../services/view/viewEndpoints";
import HeButton from "../../../shared/ui/buttons/HeButton";
import ViewIcon from "../../../shared/ui/icons/IconPicker/ViewIcon";
import Checkbox from "../../../shared/ui/inputs/checkbox/Checkbox";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import notify from "../../../utils/notify";
import TypeSelect from "./TypeSelect";

type Props = {
  open: boolean;
  setOpen:
  | ((value: boolean) => void)
  | (((value: boolean) => void) & React.FormEvent<HTMLDivElement>);
  defaultType?: string;
  defaultName?: string;
  disableType?: boolean;
  onClick?: any;
};

const CreateView = ({
  onClick,
  open,
  setOpen,
  defaultType,
  defaultName,
  disableType = false,
}: Props) => {
  const [name, setName] = useState<string>(defaultName || "");
  const [description, setDescription] = useState<string>("");
  const [type, setType] = useState<any>(defaultType || "");
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [selectedIcon, setSelectedIcon] = useState<{ type: 'emoji' | 'built_in', value: string } | undefined>();
  const [iconHovered, setIconHovered] = useState<boolean>(false);
  const currentUser = useAuthInfo().user;
  const currentWorkspace = useWorkspaceStore((state) => state.workspace);
  // No longer need ref with Mantine Modal
  const navigate = useNavigate();

  const { data: views } : any = useViews({
    workspaceId: currentWorkspace?.id,
    enabled: !!currentWorkspace?.id,
  });

  const createViewMutation = useMutation({
    mutationFn: createView,
  });

  const accountType = useMemo(
    () => ({
      ...views?.filter((item: any) => item?.is_precreated)?.find((val: any) => val?.entity_relation === "accounts"),
      owner_id: currentUser?.userId,
      workspace_id: currentWorkspace?.id,
      is_precreated: false,
    }),
    [currentUser?.userId, currentWorkspace?.id, views],
  );

  const contactType = useMemo(
    () => ({
      ...views?.filter((item: any) => item?.is_precreated)?.find((val: any) => val?.entity_relation === "users"),
      owner_id: currentUser?.userId,
      workspace_id: currentWorkspace.id,
      is_precreated: false,
    }),
    [currentUser?.userId, currentWorkspace.id, views],
  );

  // No need for manual click outside detection with Mantine Modal

  const onTypeClick = (select: string) => {
    setType(select);
    
    // Set default icon based on type
    if (select === "accounts" && !selectedIcon) {
      setSelectedIcon({ type: 'built_in', value: 'Building' });
    } else if (select === "users" && !selectedIcon) {
      setSelectedIcon({ type: 'built_in', value: 'Users' });
    }
  };

  const submitHandler = () => {
    if (currentWorkspace.id) {
      let viewData: any;
      
      // Prepare icon data based on selection
      // Make sure we're using the CURRENT selectedIcon value, not a stale one
      const iconData = selectedIcon ? {
        // Use the type directly from the selectedIcon
        icon_type: selectedIcon.type,
        // Use icon_key for both emoji and icon names
        icon_key: selectedIcon.value,
        // For compatibility with existing code
        view_icon: selectedIcon.value,
        // icon_asset_id would only be used for custom uploaded icons
        icon_asset_id: undefined
      } : {};
      
      // Prepare the view data with the selected icon
      
      if (type === "accounts") {
        // Remove any existing icon data from accountType to avoid conflicts
        // Using destructuring with camelCase variables to satisfy ESLint naming convention
        const { icon_type: iconType, icon_key: iconKey, view_icon: viewIcon, ...cleanAccountType } = accountType;
        
        viewData = {
          ...cleanAccountType,
          view_description: description.trim() === '' ? null : description,
          view_name: name,
          is_public: isPublic,
          is_precreated: false,
          filters: {},
          ...iconData // Add the fresh icon data
        };
      } else if (type === "users") {
        // Remove any existing icon data from contactType to avoid conflicts
        // Using destructuring with camelCase variables to satisfy ESLint naming convention
        const { icon_type: iconType, icon_key: iconKey, view_icon: viewIcon, ...cleanContactType } = contactType;
        
        viewData = {
          ...cleanContactType,
          view_name: name,
          view_description: description.trim() === '' ? null : description,
          is_public: isPublic,
          is_precreated: false,
          filters: {},
          ...iconData // Add the fresh icon data
        };
      }

      // Store the current icon data for comparison in case we need to update later
      const currentIconData = {
        type: selectedIcon?.type,
        value: selectedIcon?.value
      };
      
      createViewMutation.mutate(viewData, {
        onSuccess: (data: any) => {
          // Update the query cache with the new view data, ensuring we include the correct icon
          queryClient.setQueryData(["views", currentWorkspace?.id], (oldData: any) => {
            if (!oldData) return oldData;
            
            // Create new view with the correct icon data
            const newView = {
              ...data?.data,
              owner_id: currentUser?.userId,
              // Explicitly include the current icon data
              icon_type: currentIconData.type,
              icon_key: currentIconData.value,
              view_icon: currentIconData.value
            };
            
            return {
              ...oldData,
              data: {
                ...oldData.data,
                views: [...oldData.data.views, newView],
              },
            };
          });
          
          // Also invalidate the query to ensure fresh data
          queryClient.invalidateQueries(["views", currentWorkspace?.id]);
          if (process.env.REACT_APP_HE_WORKSPACE_KEY && process.env.REACT_APP_HE_DATA_KEY) {
            window?.hyperengage(
              "track",
              "new_view_created",
              {
                properties: {
                  view_name: name,
                  view_description: description,
                  is_public: isPublic,
                  created_by: currentUser?.userId,
                  workspace_id: currentWorkspace?.id,
                  entity_type: type,
                  has_icon: !!selectedIcon,
                },
              },
            );
          }
          notify({
            heading: "New View Created",
            type: "success",
            messageList: ["You have successfully created a new view."],
          });
          // Short delay before navigation to ensure data is refreshed
          setTimeout(() => {
            navigate(`/home/${currentWorkspace?.unique_identifier}/view/${data?.data.id}`);
          }, 100);
        },
        onError: (error: any) => {
          if (error.response?.status !== 0) {
            notify({
              heading: "Unknown error.",
              type: "failure",
              messageList: [
                "An unknown error occurred while creating the new view. Please try again later or contact our support team for assistance.",
              ],
            });
          }
        },
      });
    }
  };

  return (
    <Modal
      opened={open}
      onClose={() => setOpen(false)}
      title="New View"
      size="lg"
      centered
      overlayProps={{
        blur: 3,
        opacity: 0.55,
      }}
      styles={{
        header: {
          padding: '20px 20px 0px 20px',
        },
        body: {
          padding: '10px 20px 20px 20px',
        },
        title: {
          fontSize: '20px',
          fontWeight: 'bold',
          padding: '0px',
        },
      }}
      withCloseButton={false}
    >
      <section
        className="flex flex-col gap-3 bg-white"
      >
            
            <div className="flex gap-6 items-end">
              <div className="flex flex-col items-center gap-2">
                <div 
                  className="relative cursor-pointer" 
                  onMouseEnter={() => setIconHovered(true)}
                  onMouseLeave={() => setIconHovered(false)}
                >
                  <ViewIcon
                    selectedIcon={selectedIcon}
                    onSelect={setSelectedIcon}
                    size={40}
                    className={`flex-shrink-0 transition-all duration-200 ${iconHovered ? 'scale-105 shadow-md' : ''}`}
                  />
                </div>
              </div>
              <div className="flex-grow">
                <TextInput
                  value={name}
                  onChange={(e: any) => setName(e.target.value)}
                  classNames={{
                    label: "leading-1/2 text-gray-500 text-xs",
                  }}
                  id="viewName"
                  label="View name"
                  type="text"
                  className="w-full"
                />
              </div>
            </div>
            <div className="">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="view_description" className="block text-xs mb-1 text-gray-500">
                Description
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                id="view_description"
                placeholder="Add a description for your view"
                minRows={5}
                classNames={{
                  input: "!p-3 bg-gray-50",
                }}
              />
            </div>
            <div className="flex w-full items-center justify-between gap-4 mb-1">
              <TypeSelect
                selected={type === "accounts"}
                disabled={defaultType === "users" && disableType}
                onClick={onTypeClick}
                type="accounts"
              />
              <TypeSelect
                selected={type === "users"}
                disabled={defaultType === "accounts" && disableType}
                onClick={onTypeClick}
                type="users"
              />
            </div>
            <div className="flex justify-between align-center mt-6 gap-4">
              <div className="px-2 flex items-center">
                <Checkbox
                  checked={isPublic}
                  onChange={() => setIsPublic(!isPublic)}
                  id="isPublic"
                >
                  Do you want your view to be public?
                </Checkbox>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="border-none text-purple-600 hover:text-purple-800 font-medium transition-colors duration-150"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  Back
                </button>
                <HeButton
                  color="purple"
                  disabled={!type || !name}
                  text="Create view"
                  size="sm"
                  onClick={() => {
                    if (onClick) {
                      onClick(name, type, isPublic);
                      setOpen(false);
                    } else {
                      submitHandler();
                    }
                  }}
                />
              </div>
            </div>
      </section>
    </Modal>
  );
};

export default CreateView;
